import React, { useEffect, useState } from 'react';
import { useNotify } from '../components/NotificationProvider';
import {
  getHorarioSemanal, upsertDiaSemanal,
  getBloqueos, createBloqueo, deleteBloqueo
} from '../api/agenda';
import { isAdminRole } from '../utils/helpers';

const SHOW_BLOQUEOS = false; // UI de bloqueos deshabilitada

const dias = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

// Función para normalizar valores de hora para inputs de tipo "time" (debe ser HH:mm)
const normalizeTimeForInput = (timeValue) => {
  if (!timeValue) return '';
  const str = String(timeValue).trim();
  if (str === '') return '';
  
  // Si ya está en formato HH:mm, devolverlo directamente
  if (/^\d{2}:\d{2}$/.test(str)) {
    return str;
  }
  
  // Si está en formato HH:mm:ss, tomar solo HH:mm
  if (/^\d{1,2}:\d{2}(?::\d{2})?$/.test(str)) {
    return str.slice(0, 5);
  }
  
  // Intentar parsear como objeto Date si es ISO
  try {
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  } catch (e) {
    // Ignorar errores de parsing
  }
  
  // Si no se puede parsear, devolver cadena vacía
  console.warn('No se pudo normalizar el valor de hora:', timeValue);
  return '';
};

// Convierte cualquier valor aceptado por normalizeTimeForInput a HH:mm:ss
const toHHMMSS = (val) => {
  const hhmm = normalizeTimeForInput(val);
  if (!hhmm) return null;
  const [hh, mm] = hhmm.split(':');
  const H = String(hh).padStart(2, '0');
  const M = String(mm).padStart(2, '0');
  return `${H}:${M}:00`;
};

// Convierte a formato de precisión 7: HH:mm:ss.0000000
const toTime7 = (val) => {
  const s = toHHMMSS(val);
  if (!s) return null;
  const parts = s.split(':');
  if (parts.length !== 3) return null;
  const [hh, mm, ss] = parts;
  return `${hh}:${mm}:${ss}.0000000`;
};

// estado inicial por defecto (garantiza valores aunque la carga falle)
const buildDefaultHorarios = () => {
  const out = {};
  dias.forEach(d => {
    out[d.value] = {
      DiaSemana: d.value,
      Inicio: '09:00',
      Fin: '18:00',
      Activo: true,
      Id: null
    };
  });
  return out;
};

const AdminSchedule = ({ user, onLoginClick }) => {
  const { success: notifySuccess, error: notifyError, warning: notifyWarning, info: notifyInfo } = useNotify();
  const [semanal, setSemanal] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  const [formBloqueo, setFormBloqueo] = useState({ 
    DiaSemana: 1, 
    BloqueInicio: '', 
    BloqueFin: '', 
    Motivo: '' 
  });
  const [editBloqueo, setEditBloqueo] = useState(null);
  const [pendingDeleteBloqueo, setPendingDeleteBloqueo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localHorarios, setLocalHorarios] = useState(buildDefaultHorarios());

  // Normaliza varios posibles formatos que vienen del servidor a "HH:MM"
  const formatTimeFromDb = (timeVal) => {
    if (timeVal === null || timeVal === undefined) return null;
    try {
      // si es objeto Date
      if (timeVal instanceof Date) {
        return `${String(timeVal.getHours()).padStart(2,'0')}:${String(timeVal.getMinutes()).padStart(2,'0')}`;
      }
      const s = String(timeVal).trim();
      // si trae ISO datetime -> extraer parte hora
      const iso = s.match(/\d{4}-\d{2}-\d{2}T(\d{2}:\d{2}:\d{2})/);
      if (iso) return iso[1].slice(0,5);
      // si trae 'HH:mm:ss' o 'HH:mm'
      if (s.length >= 4 && s.includes(':')) return s.slice(0,5);
      return null;
    } catch (e) {
      console.error('formatTimeFromDb error', e, timeVal);
      return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

  const horarios = await getHorarioSemanal();
  console.log('Horarios recibidos (raw):', horarios);
  try { console.log('Horarios recibidos (raw, dump):', JSON.stringify(horarios, null, 2)); } catch {}

        const horariosNorm = (horarios || []).map(h => ({
          ...h,
          Inicio: formatTimeFromDb(h.HoraInicio || h.Inicio),
          Fin: formatTimeFromDb(h.HoraFin || h.Fin),
          DiaSemana: Number(h.DiaSemana)
        }));
  console.log('Horarios normalizados:', horariosNorm);
  try { console.log('Horarios normalizados (dump):', JSON.stringify(horariosNorm, null, 2)); } catch {}

        // Construir localHorarios partiendo de los valores de BD, manteniendo defaults si faltan
        const initialHorarios = buildDefaultHorarios();
        dias.forEach(d => {
          const horarioExistente = horariosNorm.find(h => Number(h.DiaSemana) === Number(d.value));
          if (horarioExistente) {
            // Normalizar valores de tiempo - asegurar que siempre tengan formato HH:mm válido
            const inicioNormalizado = horarioExistente.Inicio && String(horarioExistente.Inicio).trim() !== '' 
              ? normalizeTimeForInput(horarioExistente.Inicio) || initialHorarios[d.value].Inicio
              : initialHorarios[d.value].Inicio;
            const finNormalizado = horarioExistente.Fin && String(horarioExistente.Fin).trim() !== ''
              ? normalizeTimeForInput(horarioExistente.Fin) || initialHorarios[d.value].Fin
              : initialHorarios[d.value].Fin;
            
            initialHorarios[d.value] = {
              DiaSemana: d.value,
              Inicio: inicioNormalizado,
              Fin: finNormalizado,
              Activo: typeof horarioExistente.Activo === 'boolean' ? horarioExistente.Activo : initialHorarios[d.value].Activo,
              Id: horarioExistente.Id || null
            };
          }
        });

        console.log('Initial localHorarios to set:', initialHorarios);
        setLocalHorarios(initialHorarios);
        setSemanal(horariosNorm);

        if (SHOW_BLOQUEOS) {
          const bloqueosData = await getBloqueos();
          setBloqueos(bloqueosData || []);
        } else {
          setBloqueos([]);
        }
      } catch (error) {
        console.error('Error loading schedule:', error);
        setError('Error al cargar la agenda: ' + (error.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleUpdateLocalHorario = (diaSemana, field, value) => {
    setLocalHorarios(prev => ({
      ...prev,
      [diaSemana]: {
        ...prev[diaSemana],
        [field]: value
      }
    }));
  };

  const handleSaveDia = async (diaSemana) => {
    try {
      const horario = localHorarios[diaSemana];
      if (!horario) throw new Error('Horario no encontrado');

      // Normalizar y validar valores de inicio y fin
      let start = horario.Inicio;
      let end = horario.Fin;
      
      // Convertir a string y limpiar - manejar null, undefined, y cadenas vacías
      if (start == null || start === '') {
        console.warn('Inicio está vacío para día', diaSemana);
  return notifyWarning('Por favor completa el campo Inicio antes de guardar.');
      }
      if (end == null || end === '') {
        console.warn('Fin está vacío para día', diaSemana);
  return notifyWarning('Por favor completa el campo Fin antes de guardar.');
      }
      
      start = String(start).trim();
      end = String(end).trim();
      
      // Verificar que no estén vacíos después de trim
      if (start === '' || end === '') {
  return notifyWarning('Los campos Inicio y Fin no pueden estar vacíos.');
      }
      
      // Validar formato básico HH:mm o HH:mm:ss
      const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
      if (!timeRegex.test(start)) {
        console.error('Formato inválido para Inicio:', start);
  return notifyError(`Formato inválido para Inicio. Valor recibido: "${start}". Debe ser HH:mm o HH:mm:ss (ej: 09:00)`);
      }
      if (!timeRegex.test(end)) {
        console.error('Formato inválido para Fin:', end);
  return notifyError(`Formato inválido para Fin. Valor recibido: "${end}". Debe ser HH:mm o HH:mm:ss (ej: 18:00)`);
      }
      
      const toMinutes = s => {
        const parts = String(s).split(':').map(x => parseInt(x,10));
        if (parts.length < 2 || parts.some(isNaN)) return NaN;
        return parts[0]*60 + parts[1];
      };
      const startMin = toMinutes(start);
      const endMin = toMinutes(end);
      if (isNaN(startMin) || isNaN(endMin)) {
  return notifyError('Formato inválido de hora. Usa HH:mm');
      }
      if (startMin >= endMin) {
  return notifyWarning('El Inicio debe ser menor que el Fin.');
      }

      const ensureSeconds = s => {
        if (!s || String(s).trim() === '') {
          throw new Error('El valor de hora no puede estar vacío');
        }
        const parts = String(s).trim().split(':');
        // Validar que tenga al menos HH:mm
        if (parts.length < 2 || parts.length > 3) {
          throw new Error(`Formato de hora inválido: "${s}". Debe ser HH:mm o HH:mm:ss`);
        }
        // Validar que todas las partes sean números válidos
        const hh = parseInt(parts[0], 10);
        const mm = parseInt(parts[1], 10);
        const ss = parts[2] ? parseInt(parts[2], 10) : 0;
        if (isNaN(hh) || isNaN(mm) || isNaN(ss) || hh < 0 || hh > 23 || mm < 0 || mm > 59 || ss < 0 || ss > 59) {
          throw new Error(`Valores de hora inválidos: "${s}". Horas: 0-23, Minutos: 0-59, Segundos: 0-59`);
        }
        if (parts.length === 3) return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
        return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}:00`;
      };

      const payload = {
        DiaSemana: Number(diaSemana),
        Inicio: ensureSeconds(start),  
        Fin: ensureSeconds(end),           
        Activo: !!horario.Activo
      };

      console.log('📤 Enviando /api/agenda/semanal payload:', payload);
      console.log('📤 Valores antes de ensureSeconds - Inicio:', start, 'Fin:', end);
      await upsertDiaSemanal(payload);

      // refrescar estado desde backend
      const hs = await getHorarioSemanal();
      setSemanal(hs);

  notifySuccess('Horario guardado correctamente.');
    } catch (err) {
      console.error('Error al guardar día:', err);
      console.error('Error completo:', {
        message: err.message,
        status: err.status,
        response: err.response,
        details: err.details
      });
      
      let msg = 'Error al guardar horario';
      let detalles = '';
      
      try {
        if (err && err.message) {
          msg = err.message;
        }
        if (err && err.details) {
          detalles = '\n\nDetalles:\n' + JSON.stringify(err.details, null, 2);
        }
        if (err && err.response) {
          try {
            const responseJson = JSON.parse(err.response);
            if (responseJson.detalle) {
              detalles = '\n\nDetalles:\n' + JSON.stringify(responseJson.detalle, null, 2);
            }
          } catch (e) {
            detalles = '\n\nRespuesta del servidor:\n' + err.response;
          }
        }
      } catch (e) {
        console.error('Error procesando detalles:', e);
      }
      
  notifyError(msg + detalles);
    }
  };

  const reloadBloqueos = async () => {
    try {
      const bloq = await getBloqueos();
      setBloqueos(bloq || []);
    } catch (error) {
      console.error('Error loading bloqueos:', error);
  notifyError('Error al cargar bloqueos: ' + error.message);
    }
  };

const handleCreateBloqueo = async () => {
  const { DiaSemana, BloqueInicio, BloqueFin, Motivo } = formBloqueo;
  
  console.log('📤 Creando bloqueo con:', { DiaSemana, BloqueInicio, BloqueFin, Motivo });
  
  if (!DiaSemana || !BloqueInicio || !BloqueFin) {
    console.error('❌ Faltan datos:', { DiaSemana, BloqueInicio, BloqueFin });
  return notifyWarning('Completa Día, Inicio y Fin');
  }
  
  try {
    // Enviar en formato TIME(7) para máxima compatibilidad con SQL Server
    const ini = toTime7(BloqueInicio);
    const fin = toTime7(BloqueFin);
    if (!ini || !fin) {
  return notifyError('Formato de hora inválido. Usa HH:mm');
    }
    const payload = { 
      DiaSemana, 
      Inicio: ini,
      Fin: fin,
      Motivo 
    };
    console.log('📤 Payload createBloqueo:', payload);
    await createBloqueo(payload);
    setFormBloqueo({ DiaSemana: 1, BloqueInicio: '', BloqueFin: '', Motivo: '' });
    await reloadBloqueos();
  notifySuccess('Bloqueo creado correctamente');
  } catch (error) {
    console.error('Error creating bloqueo:', error);
  notifyError('Error al crear bloqueo: ' + error.message);
  }
};
  const handleUpdateBloqueo = async () => {
    if (!editBloqueo) return;
    
    try {
      // En el nuevo sistema, actualizar = eliminar + crear nuevo
      await deleteBloqueo(editBloqueo.Id);
      const ini = toTime7(editBloqueo.Inicio ?? editBloqueo.BloqueInicio);
      const fin = toTime7(editBloqueo.Fin ?? editBloqueo.BloqueFin);
      if (!ini || !fin) {
  return notifyError('Formato de hora inválido. Usa HH:mm');
      }
      const payload = {
        DiaSemana: editBloqueo.DiaSemana,
        Inicio: ini,
        Fin: fin,
        Motivo: editBloqueo.Motivo || ''
      };
      console.log('📤 Payload updateBloqueo:', payload);
      await createBloqueo(payload);
      setEditBloqueo(null);
      await reloadBloqueos();
  notifySuccess('Bloqueo actualizado correctamente');
    } catch (error) {
      console.error('Error updating bloqueo:', error);
  notifyError('Error al actualizar bloqueo: ' + error.message);
    }
  };

  const handleDeleteBloqueo = async (id) => {
    try {
      await deleteBloqueo(id);
      await reloadBloqueos();
      notifySuccess('Bloqueo eliminado correctamente');
    } catch (error) {
      console.error('Error deleting bloqueo:', error);
      notifyError('Error al eliminar bloqueo: ' + error.message);
    }
  };

  // Si no hay usuario admin
  if (!user || !isAdminRole(user.role)) {
    return (
      <div className="text-center py-20 bg-white rounded-3xl shadow-xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Acceso Restringido</h2>
        <p className="text-gray-600 mb-6">Solo administradores pueden acceder a esta página.</p>
        <button 
          onClick={onLoginClick} 
          className="px-8 py-3 bg-purple-400 text-white font-bold rounded-xl shadow-lg hover:bg-purple-500 transition-all duration-300"
        >
          Iniciar Sesión
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-lg text-purple-600">Cargando agenda...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <div className="text-red-600 text-lg mb-4">Error</div>
        <div className="text-gray-700 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 text-purple-700">Agenda de atención</h1>

      {/* Horario semanal */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4 text-purple-700">Horario Semanal</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dias.map(d => {
            const horario = localHorarios[d.value] || {
              DiaSemana: d.value,
              Inicio: '09:00',
              Fin: '18:00',
              Activo: true,
              Id: null
            };

            return (
              <div key={d.value} className="rounded-xl border border-purple-200 bg-white p-4 shadow">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-purple-700">{d.label}</h3>
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={!!horario.Activo} 
                      onChange={e => handleUpdateLocalHorario(d.value, 'Activo', e.target.checked)}
                      className="rounded text-purple-600"
                    />
                    Activo
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-600">Inicio</label>
                    <input 
                      type="time" 
                      value={normalizeTimeForInput(horario.Inicio) || ''}
                      onChange={(e) => handleUpdateLocalHorario(d.value, 'Inicio', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Fin</label>
                    <input 
                      type="time" 
                      value={normalizeTimeForInput(horario.Fin) || ''}
                      onChange={(e) => handleUpdateLocalHorario(d.value, 'Fin', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleSaveDia(d.value)} 
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    Guardar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {SHOW_BLOQUEOS && (
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4 text-purple-700">Bloqueos de Tiempo (Horarios no disponibles)</h2>
        
        {/* Formulario para crear bloqueo */}
        <div className="rounded-xl border border-purple-200 bg-white p-4 shadow mb-6">
          <h3 className="font-semibold text-purple-700 mb-3">Agregar Nuevo Bloqueo</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-600">Día de la semana</label>
              <select 
                value={formBloqueo.DiaSemana} 
                onChange={e => setFormBloqueo(s => ({ ...s, DiaSemana: Number(e.target.value) }))} 
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {dias.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Inicio</label>
              <input 
                type="time" 
                value={formBloqueo.BloqueInicio} 
                onChange={e => setFormBloqueo(s => ({ ...s, BloqueInicio: e.target.value }))} 
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Fin</label>
              <input 
                type="time" 
                value={formBloqueo.BloqueFin} 
                onChange={e => setFormBloqueo(s => ({ ...s, BloqueFin: e.target.value }))} 
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Motivo</label>
              <input 
                type="text" 
                value={formBloqueo.Motivo} 
                onChange={e => setFormBloqueo(s => ({ ...s, Motivo: e.target.value }))} 
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Opcional"
              />
            </div>
          </div>
          <div className="mt-3">
            <button 
              onClick={handleCreateBloqueo} 
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            >
              Agregar bloqueo
            </button>
          </div>
        </div>

        {/* Lista de bloqueos */}
        <div className="rounded-xl border border-purple-200 bg-white p-4 shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-purple-700">Bloqueos Configurados</h3>
            <button 
              onClick={reloadBloqueos} 
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            >
              Actualizar
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-purple-100">
              <thead className="bg-purple-50">
                <tr>
                  <th className="px-3 py-2 text-xs font-bold text-purple-700 text-left">Día</th>
                  <th className="px-3 py-2 text-xs font-bold text-purple-700 text-left">Rango</th>
                  <th className="px-3 py-2 text-xs font-bold text-purple-700 text-left">Motivo</th>
                  <th className="px-3 py-2 text-xs font-bold text-purple-700 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {bloqueos.length > 0 ? (
                  bloqueos.map(b => (
                    <tr key={b.Id} className="hover:bg-purple-50">
                      <td className="px-3 py-2 text-sm">
                        {dias.find(d => d.value === b.DiaSemana)?.label || `Día ${b.DiaSemana}`}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {normalizeTimeForInput(b.Inicio ?? b.BloqueInicio)} - {normalizeTimeForInput(b.Fin ?? b.BloqueFin)}
                      </td>
                      <td className="px-3 py-2 text-sm">{b.Motivo || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => setEditBloqueo(b)} 
                            className="px-3 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors text-sm"
                          >
                            Editar
                          </button>
                          <button 
                            onClick={() => setPendingDeleteBloqueo(b)} 
                            className="px-3 py-1 bg-rose-100 text-rose-700 rounded hover:bg-rose-200 transition-colors text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-purple-500">
                      No hay bloqueos configurados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* Modal de edición de bloqueo */}
      {SHOW_BLOQUEOS && editBloqueo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-purple-700">Editar bloqueo</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm text-gray-600">Día de la semana</label>
                <select 
                  value={editBloqueo.DiaSemana} 
                  onChange={(e) => setEditBloqueo({ ...editBloqueo, DiaSemana: Number(e.target.value) })} 
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {dias.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600">Inicio</label>
                <input 
                  type="time" 
                  value={normalizeTimeForInput(editBloqueo.Inicio ?? editBloqueo.BloqueInicio) || ''} 
                  onChange={(e) => setEditBloqueo({ ...editBloqueo, Inicio: e.target.value, BloqueInicio: e.target.value })} 
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Fin</label>
                <input 
                  type="time" 
                  value={normalizeTimeForInput(editBloqueo.Fin ?? editBloqueo.BloqueFin) || ''} 
                  onChange={(e) => setEditBloqueo({ ...editBloqueo, Fin: e.target.value, BloqueFin: e.target.value })} 
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm text-gray-600">Motivo</label>
                <input 
                  type="text" 
                  value={editBloqueo.Motivo || ''} 
                  onChange={(e) => setEditBloqueo({ ...editBloqueo, Motivo: e.target.value })} 
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Opcional"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button 
                onClick={() => setEditBloqueo(null)} 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleUpdateBloqueo} 
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {SHOW_BLOQUEOS && pendingDeleteBloqueo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2 text-rose-700">Confirmar eliminación</h3>
            <p className="text-sm text-gray-600 mb-4">
              ¿Seguro que deseas eliminar el bloqueo del día <strong>{dias.find(d => d.value === pendingDeleteBloqueo.DiaSemana)?.label}</strong> 
              ({normalizeTimeForInput(pendingDeleteBloqueo.Inicio ?? pendingDeleteBloqueo.BloqueInicio)} - {normalizeTimeForInput(pendingDeleteBloqueo.Fin ?? pendingDeleteBloqueo.BloqueFin)})?
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setPendingDeleteBloqueo(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  await handleDeleteBloqueo(pendingDeleteBloqueo.Id);
                  setPendingDeleteBloqueo(null);
                }}
                className="px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSchedule;
